import cn from 'classnames';
import { FC, FormEvent, useState } from 'react';
import { AssessmentInstrument, AssessmentWindow } from 'src/api/assessments';

import styles from './Assessment.module.scss';
import {
  CONSENT_RESEARCH_TEXT,
  CONSENT_SERVICE_TEXT,
  DURATION_NOTE,
  FINAL_NOTE,
  LATER_LABEL,
  SUBMIT_LABEL,
  WINDOW_KICKER,
} from './assessmentText';

export interface AssessmentFormValues {
  answers: Record<string, number>;
  consentResearch: boolean;
}

interface AssessmentFormProps {
  instrument: AssessmentInstrument;
  window: AssessmentWindow;
  /** Whether the research opt-in starts ticked (the member's standing word). */
  initialConsentResearch?: boolean;
  submitting?: boolean;
  onSubmit: (values: AssessmentFormValues) => void;
  /** Shown as a quiet link when the member may put the questions off. */
  onLater?: () => void;
}

/**
 * The three questions with their five answers, the programme consent and
 * the research opt-in. Sending needs every question answered and the
 * programme consent ticked; the research tick is the member's choice.
 */
export const AssessmentForm: FC<AssessmentFormProps> = ({
  instrument,
  window,
  initialConsentResearch = false,
  submitting = false,
  onSubmit,
  onLater,
}) => {
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [consentService, setConsentService] = useState(false);
  const [consentResearch, setConsentResearch] = useState(
    initialConsentResearch
  );

  const complete =
    instrument.items.every((item) => answers[item.id] !== undefined) &&
    consentService;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!complete || submitting) {
      return;
    }
    onSubmit({ answers, consentResearch });
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <div className={styles.formHeader}>
        <div className={styles.kicker}>{WINDOW_KICKER[window]}</div>
        <h2 className={styles.formTitle}>{instrument.title}</h2>
        <p className={styles.text}>
          {instrument.instruction} {DURATION_NOTE}
        </p>
      </div>

      {instrument.items.map((item, index) => (
        <fieldset key={item.id} className={styles.question}>
          <legend className={styles.questionText}>
            {index + 1}. {item.text}
          </legend>
          <div className={styles.scale}>
            {instrument.scale.map((point) => {
              const selected = answers[item.id] === point.value;
              return (
                <label
                  key={point.value}
                  className={cn(styles.pill, {
                    [styles.pillSelected]: selected,
                  })}
                >
                  <input
                    className={styles.hiddenInput}
                    type="radio"
                    name={`assessment-${item.id}`}
                    value={point.value}
                    checked={selected}
                    onChange={() =>
                      setAnswers((previous) => ({
                        ...previous,
                        [item.id]: point.value,
                      }))
                    }
                  />
                  {point.label}
                </label>
              );
            })}
          </div>
        </fieldset>
      ))}

      <div className={styles.consents}>
        <label className={styles.consent}>
          <input
            type="checkbox"
            className={styles.checkbox}
            checked={consentService}
            onChange={(event) => setConsentService(event.target.checked)}
          />
          <span>{CONSENT_SERVICE_TEXT}</span>
        </label>
        <label className={styles.consent}>
          <input
            type="checkbox"
            className={styles.checkbox}
            checked={consentResearch}
            onChange={(event) => setConsentResearch(event.target.checked)}
          />
          <span>{CONSENT_RESEARCH_TEXT}</span>
        </label>
      </div>

      <div className={styles.formFooter}>
        <span className={styles.quiet}>{FINAL_NOTE}</span>
        <div className={styles.formActions}>
          {onLater ? (
            <button
              type="button"
              className={styles.linkButton}
              onClick={onLater}
              disabled={submitting}
            >
              {LATER_LABEL}
            </button>
          ) : null}
          <button
            type="submit"
            className={styles.primaryButton}
            disabled={!complete || submitting}
          >
            {SUBMIT_LABEL}
          </button>
        </div>
      </div>
    </form>
  );
};
